// Custom Components
import DonutChart from "components/DonutChart";
import PdmsNavbar from "components/navbars/PdmsNavbar";
import MonthNavigator from "components/ui/MonthNavigator";
import HorizontalBarChart from "components/ui/HorizontalBarChart";
import LoadingScreen from "components/LoadingScreen";
// React Tools
import { useEffect, useMemo, useState } from "react";
// Api Calling
import { fetchWatomsPdmsDashboard } from "services/pdms";
import { fetchSchools } from "services/data";
import { fetchWatomsClasses, fetchWatomsSubjects, fetchWatomsTrainers } from "services/watoms/watomsData";
// Custom Utils
import { roundNumber } from "utils/roundNumber";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartColumn } from "@fortawesome/free-solid-svg-icons";
import WatomsPdmsReportSheet from "../../../components/watoms/pdms/WatomsPdmsReportSheet";
import ModelNaviagtionSideBar from "components/ui/ModelNavigationSideBar";

const currentMonth = new Date().getMonth() + 1;
const CURRENT_YEAR = new Date().getFullYear();

const WatomsPdmsDashboard = () => {
    // unfiltered data
    const [unfilteredDashboardData, setunfilteredDashboardData] = useState(null);
    const [unfilteredTrainers, setUnfilteredTrainers] = useState([]);
    const [unfilteredClasses, setUnfilteredClasses] = useState([]);
    const [unfilteredSubjects, setUnfilteredSubjects] = useState([]);
    // data
    const [interviewAvg, setInterviewAvg] = useState(0);
    const [croAvg, setCroAvg] = useState(0);
    const [pedagogcialAvg, setPedagogcialAvg] = useState(0);
    const [teacherSelfAvg, setTeacherSelfAvg] = useState(0);
    const [studentTeacherAvg, setStudentTeacherAvg] = useState(0);
    const [managerTeacherAvg, setManagerTeacherAvg] = useState(0);
    const [hrAvg, setHrAvg] = useState(0);
    const [startingScore, setStartingScore] = useState(0);
    const [groupedFieldScores, setGroupedFieldScores] = useState({
        cro: [],
        interview: [],
        pedagogical: []
    });
    // navigator
    const [selectedMonthIdx, setSelectedMonthIdx] = useState(currentMonth);
    // filters data
    const [vtcs, setVtcs] = useState([]);
    // selected filters
    const [selectedVtc, setSelectedVtc] = useState("");
    const [selectedTrainer, setSelectedTrainer] = useState("");
    const [selectedSubject, setSelectedSubject] = useState("");
    const [selectedClass, setSelectedClass] = useState("");
    // Helpers
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // status
    const [reportSheet, setReportSheet] = useState(false);

    const groupFieldScoresBySource = (dashboardData) => {
        if (!dashboardData) {
            return { cro: [], interview: [], pedagogical: [] };
        }

        const pickLatest = (items = []) => {
            if (!items.length) return [];
            return items.map(i => i.field_scores || []).flat();
        };

        return {
            cro: pickLatest(dashboardData.croForms),
            interview: pickLatest(dashboardData.interviewScores),
            pedagogical: pickLatest(dashboardData.pedagogicalScores)
        };
    };

    function filterObjectArraysByUserIds(sourceObject, allowedUserIds) {
        if (!sourceObject || !Array.isArray(allowedUserIds)) return sourceObject;

        const allowedSet = new Set(allowedUserIds);

        return Object.fromEntries(
            Object.entries(sourceObject).map(([key, value]) => [
                key,
                Array.isArray(value)
                    ? value.filter(item => allowedSet.has(item.user_id))
                    : value,
            ])
        );
    }

    const getUniqueUserIdsFromDashboard = (data) => {
        const ids = [
            ...(data?.interviewScores?.map(x => x.user_id) ?? []),
            ...(data?.croForms?.map(x => x.user_id) ?? []),
            ...(data?.pedagogicalScores?.map(x => x.user_id) ?? []),
        ];
        return [...new Set(ids)];
    };

    const filterScores = (selectedMonth, data) => {
        const filteredInterview = data?.interviewScores?.filter(interview => {
            const month = new Date(interview.createdAt).getMonth() + 1;
            return month === Number(selectedMonth);
        });
        const filteredCro = data?.croForms?.filter(cro => {
            const month = new Date(cro.createdAt).getMonth() + 1;
            return month === Number(selectedMonth);
        });
        const filteredPeda = data?.pedagogicalScores?.filter(peda => {
            const month = new Date(peda.createdAt).getMonth() + 1;
            return month === Number(selectedMonth);
        });
        const filteredSelf = data?.teacherSelfForms?.filter(self => {
            const month = new Date(self.createdAt).getMonth() + 1;
            return month === Number(selectedMonth);
        });
        const filteredStudnet = data?.studentTeacherForms?.filter(student => {
            const month = new Date(student.createdAt).getMonth() + 1;
            return month === Number(selectedMonth);
        });
        return {
            filteredInterview,
            filteredCro,
            filteredPeda,
            filteredSelf,
            filteredStudnet
        }
    }

    const calculateScores = (data) => {
        const interviewAvg =
            data?.filteredInterview?.length
                ? data.filteredInterview.reduce((s, o) => s + (o.score ?? 0), 0) / data.filteredInterview.length
                : 0;

        const croAvg =
            data?.filteredCro?.length
                ? data.filteredCro.reduce((s, o) => s + (o.score ?? 0), 0) / data.filteredCro.length
                : 0;

        const pedaAvg =
            data?.filteredPeda?.length
                ? data.filteredPeda.reduce((s, o) => s + (o.score ?? 0), 0) / data.filteredPeda.length
                : 0;

        const selfAvg =
            data?.filteredSelf?.length
                ? data.filteredSelf.reduce((s, o) => s + (o.score ?? 0), 0) / data.filteredSelf.length
                : 0;

        const studentAvg =
            data?.filteredStudnet?.length
                ? data.filteredStudnet.reduce((s, o) => s + (o.score ?? 0), 0) / data.filteredStudnet.length
                : 0;

        setInterviewAvg(interviewAvg);
        setCroAvg(croAvg);
        setPedagogcialAvg(pedaAvg);
        setTeacherSelfAvg(selfAvg);
        setStudentTeacherAvg(studentAvg);
    };

    function getEarliestDatePerUser(items = [], userIds = []) {
        const earliestMap = new Map();

        items.forEach(item => {
            if (!userIds.includes(item.user_id)) return;

            const date = new Date(item.createdAt);
            if (date.getFullYear() !== CURRENT_YEAR) return;

            const existing = earliestMap.get(item.user_id);

            if (!existing || date < existing) {
                earliestMap.set(item.user_id, date);
            }
        });

        return earliestMap; // Map<user_id, Date>
    }

    function filterStatingScores(userIds, data) {
        if (!data || !Array.isArray(userIds)) return {};

        const interview = data.interviewScores || [];
        const cro = data.croForms || [];
        const peda = data.pedagogicalScores || [];

        const allItems = [...interview, ...cro, ...peda];
        const earliestDateMap = getEarliestDatePerUser(allItems, userIds);

        const isSameDay = (d1, d2) =>
            d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate();

        const filterFn = item => {
            const earliestDate = earliestDateMap.get(item.user_id);
            if (!earliestDate) return false;

            return (
                new Date(item.createdAt).getFullYear() === CURRENT_YEAR &&
                isSameDay(new Date(item.createdAt), earliestDate)
            );
        };

        return {
            filteredInterview: interview.filter(filterFn),
            filteredCro: cro.filter(filterFn),
            filteredPeda: peda.filter(filterFn),
            earliestDates: Object.fromEntries(earliestDateMap),
        };
    }

    const calculateStartingScore = (data) => {
        const interviewAvg =
            data?.filteredInterview?.length
                ? data.filteredInterview.reduce((s, o) => s + (o.score ?? 0), 0) / data.filteredInterview.length
                : 0;

        const croAvg =
            data?.filteredCro?.length
                ? data.filteredCro.reduce((s, o) => s + (o.score ?? 0), 0) / data.filteredCro.length
                : 0;

        const pedaAvg =
            data?.filteredPeda?.length
                ? data.filteredPeda.reduce((s, o) => s + (o.score ?? 0), 0) / data.filteredPeda.length
                : 0;

        setStartingScore(
            roundNumber(
                (interviewAvg * 0.2) +
                (croAvg * 0.6) +
                (pedaAvg * 0.2)
            )
        );
    };

    const toggleMonth = (status) => {
        const nextMonth = status
            ? Math.min(selectedMonthIdx + 1, currentMonth)
            : Math.max(selectedMonthIdx - 1, 1);

        setSelectedMonthIdx(nextMonth);
    };

    const selectedClassObj = unfilteredClasses.find(
        c => c.id === Number(selectedClass)
    );

    const filteredClasses = useMemo(() => {
        if (!unfilteredClasses?.length) return [];

        return unfilteredClasses.filter(singleClass => {
            const matchesVtc =
                !selectedVtc || selectedVtc === "All"
                    ? true
                    : singleClass.organization === Number(selectedVtc);

            return matchesVtc;
        });
    }, [unfilteredClasses, selectedVtc]);

    const filteredTrainers = useMemo(() => {
        if (!unfilteredTrainers?.length) return [];

        return unfilteredTrainers.filter(trainer => {
            const matchesVtc =
                !selectedVtc || selectedVtc === "All"
                    ? true
                    : trainer.organization_id === Number(selectedVtc);

            const matchesSubject =
                !selectedSubject || selectedSubject === "All"
                    ? true
                    : trainer.teacher?.subject_id === Number(selectedSubject);
            ;
            const matchesClass =
                !selectedClass
                    ? true
                    : trainer.user_id === selectedClassObj?.trainer_user_id;

            return matchesVtc && matchesSubject && matchesClass;
        });
    }, [unfilteredTrainers, selectedVtc, selectedSubject, selectedClass]);

    const filteredSubjects = useMemo(() => {
        if (!unfilteredSubjects?.length) return [];

        return unfilteredSubjects.filter(subject => {
            const matchesVtc =
                !selectedVtc || selectedVtc === "All"
                    ? true
                    : subject.organizations.includes(Number(selectedVtc));

            return matchesVtc;
        });
    }, [unfilteredSubjects, selectedVtc, selectedSubject]);

    const filteredDashboardData = useMemo(() => {
        if (!unfilteredDashboardData) return null;

        // Start from raw data
        let result = unfilteredDashboardData;

        // Filter by VTC -> use trainers belonging to that VTC
        if (selectedVtc && selectedVtc !== "All") {
            const trainerIds = filteredTrainers.map(t => t.user_id);

            if (trainerIds.length === 0) return result;

            result = filterObjectArraysByUserIds(result, trainerIds);
        }

        // Filter by a specific subject (if selected)
        if (selectedSubject) {
            const filterTrainers = filteredTrainers.filter(t => t.teacher.subject_id === Number(selectedSubject));
            const trainerIds = filterTrainers.map(t => t.user_id);

            if (trainerIds.length === 0) return result;

            result = filterObjectArraysByUserIds(result, trainerIds);
        }

        // Filter by a specific subject (if selected)
        if (selectedClass) {
            const selectedClassObj = unfilteredClasses.find(
                c => c.id === Number(selectedClass)
            );

            if (selectedClassObj?.trainer_user_id) {
                result = filterObjectArraysByUserIds(
                    result,
                    [selectedClassObj.trainer_user_id]
                );
            }
        }

        // Filter by a specific trainer (if selected)
        if (selectedTrainer) {
            result = filterObjectArraysByUserIds(result, [Number(selectedTrainer)]);
        }

        return result;
    }, [unfilteredDashboardData, selectedVtc, selectedTrainer, filteredTrainers, selectedSubject, selectedClass]);

    const monthlyFilteredScores = useMemo(() => {
        if (!filteredDashboardData) return null;
        return filterScores(selectedMonthIdx, filteredDashboardData);
    }, [filteredDashboardData, selectedMonthIdx]);

    useEffect(() => {
        const loadPdmsData = async () => {
            const response = await fetchWatomsPdmsDashboard();
            setunfilteredDashboardData(response);
        };

        const loadVtcs = async () => {
            const response = await fetchSchools();
            const filteredVtcs = response.filter(
                vtc => ![1, 2, 12, 14].includes(vtc.id)
            );
            setVtcs(filteredVtcs);
        };

        const loadSubjects = async () => {
            const response = await fetchWatomsSubjects();
            setUnfilteredSubjects(response);
        };

        const loadClasses = async () => {
            const response = await fetchWatomsClasses();
            setUnfilteredClasses(response);
        };

        const loadTrainers = async () => {
            const response = await fetchWatomsTrainers();
            setUnfilteredTrainers(response);
        };

        const loadAllData = async () => {
            try {
                setLoading(true);

                await Promise.all([
                    loadPdmsData(),
                    loadVtcs(),
                    loadSubjects(),
                    loadClasses(),
                    loadTrainers(),
                ]);

            } catch (err) {
                console.error("API Error:", err);
                setError(err);
            } finally {
                setLoading(false); // ✅ ONLY HERE
            }
        };

        loadAllData();
    }, []);

    useEffect(() => {
        setSelectedTrainer("");
    }, [selectedVtc]);

    useEffect(() => {
        if (!filteredDashboardData || !monthlyFilteredScores) return;

        calculateScores(monthlyFilteredScores);

        const uniqueUserIds = getUniqueUserIdsFromDashboard(filteredDashboardData);
        const startingData = filterStatingScores(uniqueUserIds, filteredDashboardData);
        calculateStartingScore(startingData);

        // 🔥 GROUP field scores (no math)
        const grouped = groupFieldScoresBySource(filteredDashboardData);
        setGroupedFieldScores(grouped);

    }, [filteredDashboardData, monthlyFilteredScores]);

    const data3 = [
        { id: 1, name: 'المدير', performance: 0 },
        { id: 2, name: 'HR', performance: 0 },
        { id: 3, name: 'المدرب', performance: roundNumber(teacherSelfAvg) },
    ];

    const data2 = [
        { id: 4, name: 'المقيمين', performance: roundNumber(croAvg) },
        { id: 5, name: 'الطالب', performance: roundNumber(studentTeacherAvg) },
        { id: 6, name: 'المدرب', performance: roundNumber(teacherSelfAvg) },
    ];

    if (loading) return <LoadingScreen />;

    return (
        <div>
            <ModelNaviagtionSideBar system="watoms" />
            <PdmsNavbar>
                <button
                    className="rounded-full w-10 h-10 flex justify-center items-center bg-white/80 hover:bg-gray-200 shadow transition-all"
                    title="pdms "
                    onClick={() => setReportSheet(true)}
                >
                    <FontAwesomeIcon
                        icon={faChartColumn}
                        className="text-xl text-watomsBlue"
                    />
                </button>
            </PdmsNavbar>
            <div className="w-full h-[88vh] flex flex-col gap-[15%] text-white bg-[#1e293b] p-4">
                <div className="flex justify-evenly">
                    <div className="p-2">
                        <MonthNavigator
                            selectedMonthIdx={selectedMonthIdx}
                            onPrev={() => toggleMonth(false)}
                            onNext={() => toggleMonth(true)}
                        />
                    </div>
                    {/* To Date */}
                    {/* <div className="flex items-center gap-2">
                        <input
                            type="date"
                            className="rounded-md px-3 py-2 text-sm bg-white text-black focus:outline-none"
                        />
                        <label className="text-sm text-white m-0">إلى</label>
                    </div> */}
                    {/* From Date */}
                    {/* <div className="flex items-center gap-2">
                        <input
                            type="date"
                            className="rounded-md px-3 py-2 text-sm bg-white text-black focus:outline-none"
                        />
                        <label className="text-sm text-white m-0">من</label>
                    </div> */}
                    {/* trainer's filter */}
                    <select
                        className="border-black p-2 border-2 rounded text-center text-sm font-bold bg-white text-black cursor-pointer"
                        defaultValue=""
                        onChange={(e) => setSelectedTrainer(e.target.value)}
                    >
                        <option value="" disabled>
                            الرجاء اختيار المدرب
                        </option>
                        {filteredTrainers.map(trainer => (
                            <option key={trainer.user_id} value={trainer.user_id}>{trainer.first_name} {trainer.middle_name} {trainer.last_name}</option>
                        ))}
                    </select>
                    {/* Classes's filter */}
                    <select
                        className="border-black p-2 border-2 rounded text-center text-sm font-bold bg-white text-black cursor-pointer"
                        defaultValue=""
                        onChange={(e) => setSelectedClass(e.target.value)}
                    >
                        <option value="" disabled>
                            الرجاء اختيار الدورة التدريبية
                        </option>
                        {filteredClasses.map(singleClass => (
                            <option key={singleClass.id} value={singleClass.id}>
                                {singleClass.name}
                            </option>
                        ))}
                    </select>
                    {/* subjects / workshops filter */}
                    <select
                        className="border-black p-2 border-2 rounded text-center text-sm font-bold bg-white text-black cursor-pointer"
                        defaultValue=""
                        onChange={(e) => setSelectedSubject(e.target.value)}
                    >
                        <option value="" disabled>
                            الرجاء اختيار الورشة
                        </option>
                        <option value="All">
                            الكل
                        </option>
                        {filteredSubjects.map(subject => (
                            <option key={subject.id} value={subject.id}>{subject.name}</option>
                        ))}
                    </select>
                    {/* vtcs filter */}
                    <select
                        className="border-black p-2 border-2 rounded text-center text-sm font-bold bg-white text-black cursor-pointer"
                        defaultValue=""
                        onChange={(e) => setSelectedVtc(e.target.value)}
                    >
                        <option value="" disabled>
                            الرجاء اختيار المركز
                        </option>
                        <option value="All">
                            الكل
                        </option>
                        {vtcs.map(vtc => (
                            <option key={vtc.id} value={vtc.id}>{vtc.name}</option>
                        ))}
                    </select>
                </div>
                <div className="w-full flex justify-evenly items-center">
                    <div className="flex flex-col gap-2 items-center">
                        <h1 className="font-bold">نقطة الانطلاق</h1>
                        <DonutChart value={startingScore} size={80} color={"url(#circularRedGradient)"} />
                    </div>
                    <div className="flex flex-col gap-2 items-center">
                        <h1 className="text-lg font-bold">الفني</h1>
                        <DonutChart value={roundNumber((croAvg * 0.6) + (studentTeacherAvg * 0.3) + (teacherSelfAvg * 0.1))} size={92} color={"url(#circularGreenGradient)"} stroke={10} />
                    </div>
                    <div className="flex flex-col gap-2 items-center">
                        <h1 className="text-lg font-bold">الاداري</h1>
                        <DonutChart value={roundNumber((managerTeacherAvg * 0.4) + (hrAvg * 0.5) + (teacherSelfAvg * 0.1))} size={92} color={"url(#circularGreenGradient)"} stroke={10} />
                    </div>
                    <div className="flex flex-col gap-2 items-center">
                        <h1 className="text-2xl font-bold">التقييم العام</h1>
                        <DonutChart value={roundNumber((((croAvg * 0.6) + (studentTeacherAvg * 0.3) + (teacherSelfAvg * 0.1)) * 0.7) + (((managerTeacherAvg * 0.4) + (hrAvg * 0.5) + (teacherSelfAvg * 0.1)) * 0.3))} size={120} stroke={15} />
                    </div>
                </div>
                <div className="w-full flex justify-evenly gap-2">
                    <div className="w-[45%]"><HorizontalBarChart data={data2} title={"الفني"} /></div>
                    <div className="w-[45%]"><HorizontalBarChart data={data3} title={"الاداري"} /></div>
                </div>
            </div>
            <WatomsPdmsReportSheet
                open={reportSheet}
                title="Report"
                onClose={() => setReportSheet(false)}
            >
                {/* 6 × 6 Table */}
                <div className="mt-6" dir="rtl">
                    <h2 className="mb-3 text-base font-semibold">

                    </h2>

                    <div className="overflow-hidden rounded-lg border border-zinc-300">
                        <table className="w-full table-fixed border-collapse text-sm">
                            <thead className="bg-zinc-100">
                                <tr>
                                    <th className="border px-2 py-2 font-medium"></th>
                                    <th className="border px-2 py-2 font-medium">إرشاد و توجيه</th>
                                    <th className="border px-2 py-2 font-medium">ورشة عمل</th>
                                    <th className="border px-2 py-2 font-medium">دوائر تعلم</th>
                                    <th className="border px-2 py-2 font-medium">تعلم أقران</th>
                                    <th className="border px-2 py-2 font-medium">تدريس مصغر</th>
                                    <th className="border px-2 py-2 font-medium">تعلم ذاتي</th>
                                </tr>
                            </thead>

                            <tbody>
                                {[
                                    [
                                        "تخطيط وتصميم التدريس القائم على الجدارات",
                                        groupedFieldScores.cro.filter(f => f.field_name === '​التخطيط للتدريس القائم على الجدارات' && f.score <= 60).length + groupedFieldScores.interview.filter(f => f.field_name === 'تخطيط وتصميم التدريس القائم على الجدارات' && f.score <= 60).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === '​التخطيط للتدريس القائم على الجدارات' && f.score <= 65 && f.score > 60).length + groupedFieldScores.interview.filter(f => f.field_name === 'تخطيط وتصميم التدريس القائم على الجدارات' && f.score <= 65 && f.score > 60).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === '​التخطيط للتدريس القائم على الجدارات' && f.score <= 70 && f.score > 65).length + groupedFieldScores.interview.filter(f => f.field_name === 'تخطيط وتصميم التدريس القائم على الجدارات' && f.score <= 70 && f.score > 65).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === '​التخطيط للتدريس القائم على الجدارات' && f.score <= 75 && f.score > 70).length + groupedFieldScores.interview.filter(f => f.field_name === 'تخطيط وتصميم التدريس القائم على الجدارات' && f.score <= 75 && f.score > 70).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === '​التخطيط للتدريس القائم على الجدارات' && f.score <= 80 && f.score > 75).length + groupedFieldScores.interview.filter(f => f.field_name === 'تخطيط وتصميم التدريس القائم على الجدارات' && f.score <= 80 && f.score > 75).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === '​التخطيط للتدريس القائم على الجدارات' && f.score <= 100 && f.score > 80).length + groupedFieldScores.interview.filter(f => f.field_name === 'تخطيط وتصميم التدريس القائم على الجدارات' && f.score <= 100 && f.score > 80).length
                                    ],
                                    [
                                        "إدارة الصف ومشاركة الطلاب",
                                        groupedFieldScores.cro.filter(f => f.field_name === '​إدارة الصف الدراسي وتحفيز مشاركة المتعلمين' && f.score <= 60).length + groupedFieldScores.interview.filter(f => f.field_name === 'إدارة الصف ومشاركة الطلاب' && f.score <= 60).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === '​إدارة الصف الدراسي وتحفيز مشاركة المتعلمين' && f.score <= 65 && f.score > 60).length + groupedFieldScores.interview.filter(f => f.field_name === 'إدارة الصف ومشاركة الطلاب' && f.score <= 65 && f.score > 60).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === '​إدارة الصف الدراسي وتحفيز مشاركة المتعلمين' && f.score <= 70 && f.score > 65).length + groupedFieldScores.interview.filter(f => f.field_name === 'إدارة الصف ومشاركة الطلاب' && f.score <= 70 && f.score > 65).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === '​إدارة الصف الدراسي وتحفيز مشاركة المتعلمين' && f.score <= 75 && f.score > 70).length + groupedFieldScores.interview.filter(f => f.field_name === 'إدارة الصف ومشاركة الطلاب' && f.score <= 75 && f.score > 70).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === '​إدارة الصف الدراسي وتحفيز مشاركة المتعلمين' && f.score <= 80 && f.score > 75).length + groupedFieldScores.interview.filter(f => f.field_name === 'إدارة الصف ومشاركة الطلاب' && f.score <= 80 && f.score > 75).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === '​إدارة الصف الدراسي وتحفيز مشاركة المتعلمين' && f.score <= 100 && f.score > 80).length + groupedFieldScores.interview.filter(f => f.field_name === 'إدارة الصف ومشاركة الطلاب' && f.score <= 100 && f.score > 80).length
                                    ],
                                    [
                                        "اختيار وتصميم مصادر التعلم",
                                        groupedFieldScores.cro.filter(f => f.field_name === "​اختيار وتصميم مصادر التعلم" && f.score <= 60).length + groupedFieldScores.interview.filter(f => f.field_name === 'اختيار وتصميم مصادر التعلم' && f.score <= 60).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​اختيار وتصميم مصادر التعلم" && f.score <= 65 && f.score > 60).length + groupedFieldScores.interview.filter(f => f.field_name === 'اختيار وتصميم مصادر التعلم' && f.score <= 65 && f.score > 60).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​اختيار وتصميم مصادر التعلم" && f.score <= 70 && f.score > 65).length + groupedFieldScores.interview.filter(f => f.field_name === 'اختيار وتصميم مصادر التعلم' && f.score <= 70 && f.score > 65).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​اختيار وتصميم مصادر التعلم" && f.score <= 75 && f.score > 70).length + groupedFieldScores.interview.filter(f => f.field_name === 'اختيار وتصميم مصادر التعلم' && f.score <= 75 && f.score > 70).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​اختيار وتصميم مصادر التعلم" && f.score <= 80 && f.score > 75).length + groupedFieldScores.interview.filter(f => f.field_name === 'اختيار وتصميم مصادر التعلم' && f.score <= 80 && f.score > 75).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​اختيار وتصميم مصادر التعلم" && f.score <= 100 && f.score > 80).length + groupedFieldScores.interview.filter(f => f.field_name === 'اختيار وتصميم مصادر التعلم' && f.score <= 100 && f.score > 80).length
                                    ],
                                    [
                                        "توظيف التكنولوجيا في التعليم",
                                        groupedFieldScores.cro.filter(f => f.field_name === "​دمج وتوظيف التكنولوجيا في التعليم" && f.score <= 60).length + groupedFieldScores.interview.filter(f => f.field_name === 'توظيف التكنولوجيا في التعليم' && f.score <= 60).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​دمج وتوظيف التكنولوجيا في التعليم" && f.score <= 65 && f.score > 60).length + groupedFieldScores.interview.filter(f => f.field_name === 'توظيف التكنولوجيا في التعليم' && f.score <= 65 && f.score > 60).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​دمج وتوظيف التكنولوجيا في التعليم" && f.score <= 70 && f.score > 65).length + groupedFieldScores.interview.filter(f => f.field_name === 'توظيف التكنولوجيا في التعليم' && f.score <= 70 && f.score > 65).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​دمج وتوظيف التكنولوجيا في التعليم" && f.score <= 75 && f.score > 70).length + groupedFieldScores.interview.filter(f => f.field_name === 'توظيف التكنولوجيا في التعليم' && f.score <= 75 && f.score > 70).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​دمج وتوظيف التكنولوجيا في التعليم" && f.score <= 80 && f.score > 75).length + groupedFieldScores.interview.filter(f => f.field_name === 'توظيف التكنولوجيا في التعليم' && f.score <= 80 && f.score > 75).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​دمج وتوظيف التكنولوجيا في التعليم" && f.score <= 100 && f.score > 80).length + groupedFieldScores.interview.filter(f => f.field_name === 'توظيف التكنولوجيا في التعليم' && f.score <= 100 && f.score > 80).length
                                    ],
                                    [
                                        "أفضل استخدام لأساليب التدريس",
                                        groupedFieldScores.cro.filter(f => f.field_name === "​استخدام استراتيجيات التدريس" && f.score <= 60).length + groupedFieldScores.interview.filter(f => f.field_name === 'أفضل استخدام لأساليب التدريس' && f.score <= 60).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​استخدام استراتيجيات التدريس" && f.score <= 65 && f.score > 60).length + groupedFieldScores.interview.filter(f => f.field_name === 'أفضل استخدام لأساليب التدريس' && f.score <= 65 && f.score > 60).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​استخدام استراتيجيات التدريس" && f.score <= 70 && f.score > 65).length + groupedFieldScores.interview.filter(f => f.field_name === 'أفضل استخدام لأساليب التدريس' && f.score <= 70 && f.score > 65).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​استخدام استراتيجيات التدريس" && f.score <= 75 && f.score > 70).length + groupedFieldScores.interview.filter(f => f.field_name === 'أفضل استخدام لأساليب التدريس' && f.score <= 75 && f.score > 70).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​استخدام استراتيجيات التدريس" && f.score <= 80 && f.score > 75).length + groupedFieldScores.interview.filter(f => f.field_name === 'أفضل استخدام لأساليب التدريس' && f.score <= 80 && f.score > 75).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​استخدام استراتيجيات التدريس" && f.score <= 100 && f.score > 80).length + groupedFieldScores.interview.filter(f => f.field_name === 'أفضل استخدام لأساليب التدريس' && f.score <= 100 && f.score > 80).length
                                    ],
                                    [
                                        "تقنيات التقييم والتقويم التربوي",
                                        groupedFieldScores.cro.filter(f => f.field_name === "​التقييم والتقويم" && f.score <= 60).length + groupedFieldScores.interview.filter(f => f.field_name === 'تقنيات التقييم والتقويم التربوي' && f.score <= 60).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​التقييم والتقويم" && f.score <= 65 && f.score > 60).length + groupedFieldScores.interview.filter(f => f.field_name === 'تقنيات التقييم والتقويم التربوي' && f.score <= 65 && f.score > 60).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​التقييم والتقويم" && f.score <= 70 && f.score > 65).length + groupedFieldScores.interview.filter(f => f.field_name === 'تقنيات التقييم والتقويم التربوي' && f.score <= 70 && f.score > 65).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​التقييم والتقويم" && f.score <= 75 && f.score > 70).length + groupedFieldScores.interview.filter(f => f.field_name === 'تقنيات التقييم والتقويم التربوي' && f.score <= 75 && f.score > 70).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​التقييم والتقويم" && f.score <= 80 && f.score > 75).length + groupedFieldScores.interview.filter(f => f.field_name === 'تقنيات التقييم والتقويم التربوي' && f.score <= 80 && f.score > 75).length,
                                        groupedFieldScores.cro.filter(f => f.field_name === "​التقييم والتقويم" && f.score <= 100 && f.score > 80).length + groupedFieldScores.interview.filter(f => f.field_name === 'تقنيات التقييم والتقويم التربوي' && f.score <= 100 && f.score > 80).length
                                    ]
                                ].map((row) => (
                                    <tr key={row}>
                                        <td className="border px-2 py-2 text-center">{row[0]}</td>
                                        <td className="border px-2 py-2 text-center">{row[1]}</td>
                                        <td className="border px-2 py-2 text-center">{row[2]}</td>
                                        <td className="border px-2 py-2 text-center">{row[3]}</td>
                                        <td className="border px-2 py-2 text-center">{row[4]}</td>
                                        <td className="border px-2 py-2 text-center">{row[5]}</td>
                                        <td className="border px-2 py-2 text-center">{row[6]}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </WatomsPdmsReportSheet>
        </div>
    )
}

export default WatomsPdmsDashboard;